import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Platform,
  ActivityIndicator,
  Animated,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ionicons } from '@expo/vector-icons';
import { Post } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOptionalVideoContext } from '../contexts/VideoContext';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { createUpvote, selectMyVote } from '../services/upvotesService';
import CachedImage from './CachedImage';
import ImageCarousel from './ImageCarousel';
import VideoPlayer from './VideoPlayer';
import PixelContainer from './PixelContainer';
import LinkEmbed from './LinkEmbed';
import { fontStyle } from '../config/fonts';
import { formatShortTimestamp } from '../utils/formatTimestamp';
import { getCachedImageHeight, getImageDimensions } from '../utils/imageDimensionsCache';

interface PostListItemProps {
  post: Post;
  isDetailedPost?: boolean;
  onMorePress?: () => void;
}

const PostListItem = React.memo(function PostListItem({ post, isDetailedPost = false, onMorePress }: PostListItemProps) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const { user } = useAuth();
  const { colors } = useTheme();
  const { requireAuth } = useRequireAuth();
  const queryClient = useQueryClient();
  const videoContext = useOptionalVideoContext();

  // Fade-in animation for smooth rendering - start at 1 to prevent flicker on navigation
  const fadeAnim = useRef(new Animated.Value(1)).current;

  // Check if this post's video is the visible one (for autoplay)
  const isVideoVisible = useMemo(() => {
    if (!post.video || !videoContext) return false;
    return videoContext.visiblePostId === post.id;
  }, [post.video, post.id, videoContext?.visiblePostId]);

  // Get images array (support both new `images` and legacy `image` field)
  const postImages = useMemo(() => {
    if (post.images && post.images.length > 0) {
      return post.images;
    }
    if (post.image) {
      return [post.image];
    }
    return [];
  }, [post.images, post.image]);

  const firstImage = postImages[0];

  // Default loading height to prevent layout shift
  const DEFAULT_LOADING_HEIGHT = 200;

  // Initialize with cached height if available (prevents layout shift on navigation)
  const [imageHeight, setImageHeight] = useState<number | null>(() => {
    if (firstImage) {
      return getCachedImageHeight(firstImage);
    }
    return null;
  });

  // Track if we're still fetching dimensions - false if we already have cached height
  const [isLoadingDimensions, setIsLoadingDimensions] = useState(() => {
    if (firstImage) {
      const cachedHeight = getCachedImageHeight(firstImage);
      return cachedHeight === null;
    }
    return false;
  });

  // Calculate and cache image height
  useEffect(() => {
    if (firstImage && imageHeight === null) {
      setIsLoadingDimensions(true);
      getImageDimensions(firstImage).then((height) => {
        setImageHeight(height);
        setIsLoadingDimensions(false);
      });
    }
  }, [firstImage, imageHeight]);

  // Fetch user's vote for this post - use long staleTime to prevent refetching on navigation
  const { data: myVote } = useQuery({
    queryKey: ['myVote', post.id, user?.uid],
    queryFn: () => (user ? selectMyVote(post.id, user.uid) : null),
    enabled: !!user,
    staleTime: 1000 * 60 * 5, // 5 minutes - votes don't change often
    gcTime: 1000 * 60 * 10, // 10 minutes garbage collection time
  });

  const upvoteMutation = useMutation({
    mutationFn: (value: 1 | -1) => {
      if (!user) throw new Error('Not authenticated');
      return createUpvote(post.id, value, user.uid);
    },
    // Optimistic update - update UI immediately before Firebase confirms
    onMutate: async (value: 1 | -1) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({ queryKey: ['posts'] });
      await queryClient.cancelQueries({ queryKey: ['post', post.id] });
      await queryClient.cancelQueries({ queryKey: ['myVote', post.id, user?.uid] });

      // Snapshot previous values for rollback
      const previousPosts = queryClient.getQueryData(['posts']);
      const previousPost = queryClient.getQueryData(['post', post.id]);
      const previousVote = queryClient.getQueryData(['myVote', post.id, user?.uid]);

      // Calculate new vote state
      let newVoteValue: number | null = value;
      let scoreDelta = 0;

      if (myVote?.value === value) {
        // Clicking same vote - toggle off
        newVoteValue = null;
        scoreDelta = -value;
      } else if (myVote?.value) {
        // Switching vote (e.g., from upvote to downvote)
        scoreDelta = value - myVote.value; // e.g., -1 - 1 = -2
      } else {
        // New vote
        scoreDelta = value;
      }

      // Optimistically update myVote
      queryClient.setQueryData(['myVote', post.id, user?.uid],
        newVoteValue ? { value: newVoteValue } : null
      );

      // Optimistically update posts list
      queryClient.setQueryData(['posts'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            posts: page.posts.map((p: any) =>
              p.id === post.id
                ? { ...p, upvotesSum: (p.upvotesSum || 0) + scoreDelta }
                : p
            ),
          })),
        };
      });

      // Optimistically update single post (if in cache)
      queryClient.setQueryData(['post', post.id], (old: any) => {
        if (!old) return old;
        return { ...old, upvotesSum: (old.upvotesSum || 0) + scoreDelta };
      });

      return { previousPosts, previousPost, previousVote };
    },
    // If mutation fails, rollback to previous state
    onError: (err, value, context) => {
      console.error('Upvote mutation failed:', err);
      if (context) {
        queryClient.setQueryData(['posts'], context.previousPosts);
        queryClient.setQueryData(['post', post.id], context.previousPost);
        queryClient.setQueryData(['myVote', post.id, user?.uid], context.previousVote);
      }
    },
    // After mutation succeeds, refetch to ensure consistency with server
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
      queryClient.invalidateQueries({ queryKey: ['myVote', post.id, user?.uid] });
    },
  });

  const handleUpvote = () => {
    if (!requireAuth()) return;
    upvoteMutation.mutate(1);
  };

  const handleDownvote = () => {
    if (!requireAuth()) return;
    upvoteMutation.mutate(-1);
  };

  const handlePress = () => {
    if (!isDetailedPost) {
      router.push(`/post/${post.id}`);
    }
  };

  const locale = i18n.language.startsWith('es') ? 'es' : 'en';

  return (
    <Pressable onPress={handlePress} style={[styles.container, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
      {/* Header */}
      <View style={styles.header}>
        <Animated.View style={{ opacity: fadeAnim }}>
          {post.group?.image ? (
            <CachedImage uri={post.group.image} style={[
              styles.groupImage,
              isDetailedPost && styles.groupImageLarge
            ]} />
          ) : (
            <View style={[
              styles.groupImage,
              styles.groupImagePlaceholder,
              isDetailedPost && styles.groupImageLarge,
              { backgroundColor: colors.surface }
            ]} />
          )}
        </Animated.View>
        <View style={styles.headerTextContainer}>
          <View style={styles.headerText}>
            <Animated.Text 
              style={[
                styles.groupName, 
                fontStyle, 
                { color: colors.primary, opacity: fadeAnim }
              ]}
            >
              {post.group?.name || 'Unknown'}
            </Animated.Text>
            <Animated.Text style={[styles.timestamp, fontStyle, { color: colors.textSecondary, opacity: fadeAnim }]}>
              {formatShortTimestamp(post.createdAt, locale, false)}
            </Animated.Text>
          </View>
          {post.user?.username && (
            <Animated.Text style={[styles.username, fontStyle, { color: colors.textSecondary, opacity: fadeAnim }]}>
              {t('common.by')} @{post.user.username}
            </Animated.Text>
          )}
        </View>
        <Pressable>
          <PixelContainer backgroundColor={colors.primary} cornerSize={3} style={styles.joinButton}>
            <Text style={[styles.joinButtonText, fontStyle]}>{t('common.join')}</Text>
          </PixelContainer>
        </Pressable>
      </View>

      {/* Content */}
      <Animated.Text style={[styles.title, fontStyle, { color: colors.text, opacity: fadeAnim }]}>
        {post.title}
      </Animated.Text>

      {/* Video - wrapped to prevent tap propagation to parent Pressable */}
      {post.video && (
        <Pressable onPress={(e) => e.stopPropagation()} style={[styles.postImage]}>
          <VideoPlayer
            uri={post.video}
            autoplay={isVideoVisible || isDetailedPost}
            muted={true}
            showControls={true}
            videoId={`post-video-${post.id}`}
            isVisible={isVideoVisible || isDetailedPost}
            maxHeight={isDetailedPost ? undefined : (Platform.OS === 'web' ? 400 : 350)}
          />
        </Pressable>
      )}

      {/* Image(s) - only show if no video */}
      {!post.video && postImages.length > 0 && (
        <View style={[
          styles.postImage,
          { height: imageHeight || DEFAULT_LOADING_HEIGHT }
        ]}>
          {isLoadingDimensions ? (
            <View style={[styles.imagePlaceholder, { backgroundColor: colors.surface }]}>
              <ActivityIndicator size="small" color={colors.textSecondary} />
            </View>
          ) : (
            <ImageCarousel
              images={postImages}
              height={imageHeight || DEFAULT_LOADING_HEIGHT}
            />
          )}
        </View>
      )}

      {/* Link Embed - wrapped to prevent tap propagation to parent Pressable */}
      {post.link && (
        <Pressable onPress={(e) => e.stopPropagation()}>
          <LinkEmbed url={post.link} style={styles.linkEmbed} />
        </Pressable>
      )}

      {/* Description (only on detailed view or if no image/link) */}
      {post.description && (isDetailedPost || (postImages.length === 0 && !post.link)) && (
        <Animated.Text style={[styles.description, fontStyle, { color: colors.textSecondary, opacity: fadeAnim }]} numberOfLines={isDetailedPost ? undefined : 3}>
          {post.description}
        </Animated.Text>
      )}

      {/* Footer */}
      <Animated.View style={[styles.footer, { opacity: fadeAnim }]}>
        {/* Votes */}
        <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.voteContainer}>
          <View style={styles.voteContent}>
            <Pressable onPress={handleUpvote} style={styles.voteButton}>
              <Ionicons
                name={myVote?.value === 1 ? 'arrow-up' : 'arrow-up-outline'}
                size={20}
                color={myVote?.value === 1 ? colors.accent : colors.text}
              />
            </Pressable>
            <Text
              style={[
                styles.voteCount,
                fontStyle,
                { color: colors.textSecondary },
                myVote?.value === 1 && { color: colors.accent },
                myVote?.value === -1 && { color: colors.accent },
              ]}
            >
              {post.upvotesSum || 0}
            </Text>
            <Text style={[styles.voteDivider, fontStyle, { color: colors.border }]}>|</Text>
            <Pressable onPress={handleDownvote} style={styles.voteButton}>
              <Ionicons
                name={myVote?.value === -1 ? 'arrow-down' : 'arrow-down-outline'}
                size={20}
                color={myVote?.value === -1 ? colors.accent : colors.text}
              />
            </Pressable>
          </View>
        </PixelContainer>

        {/* Comments */}
        <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.actionButton}>
          <View style={styles.actionContent}>
            <Ionicons name="chatbubble-outline" size={18} color={colors.text} />
            <Text style={[styles.actionText, fontStyle, { color: colors.textSecondary }]}>
              {post.commentsCount || 0}
            </Text>
          </View>
        </PixelContainer>

        {/* Share */}
        <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.actionButton}>
          <Ionicons name="share-outline" size={18} color={colors.text} />
        </PixelContainer>

        {/* Delete - only for post creator and only in detail view */}
        {isDetailedPost && user?.uid === post.userId && (
          <Pressable onPress={onMorePress}>
            <PixelContainer backgroundColor={colors.surface} cornerSize={4} style={styles.actionButton}>
              <Ionicons name="trash-outline" size={18} color={colors.error} />
            </PixelContainer>
          </Pressable>
        )}
      </Animated.View>
    </Pressable>
  );
});

export default PostListItem;

const styles = StyleSheet.create({
  container: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  groupImage: {
    width: 24,
    height: 24,
    borderRadius: 12,
  },
  groupImageLarge: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  groupImagePlaceholder: {},
  headerTextContainer: {
    flex: 1,
    marginLeft: 8,
  },
  headerText: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  groupName: {
    fontSize: 13,
    fontWeight: '600',
  },
  timestamp: {
    fontSize: 11,
  },
  username: {
    fontSize: 11,
    marginTop: 2,
    opacity: 0.7,
  },
  joinButton: {
    paddingHorizontal: 12,
    paddingVertical: 2,
  },
  joinButtonText: {
    color: '#000',
    fontSize: 12,
    fontWeight: '600',
  },
  title: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  postImage: {
    width: '100%',
    minHeight: 150,
    // maxHeight limit is handled by the dynamic height calculation (up to 1000px on web)
    borderRadius: 8,
    marginBottom: 8,
    overflow: 'hidden',
    alignItems: 'center', // Center video/image when constrained by maxHeight
  },
  imagePlaceholder: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
  },
  imageContent: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
  },
  linkEmbed: {
    marginBottom: 8,
  },
  description: {
    fontSize: 14,
    lineHeight: 20,
    marginBottom: 8,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  voteContainer: {
    paddingHorizontal: 4,
    paddingVertical: 4,
  },
  voteContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  voteButton: {
    padding: 4,
  },
  voteCount: {
    fontSize: 12,
    fontWeight: '600',
    marginHorizontal: 4,
    minWidth: 24,
    textAlign: 'center',
  },
  voteDivider: {
    fontSize: 12,
    fontWeight: '600',
    marginHorizontal: 6,
  },
  actionButton: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  actionContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionText: {
    fontSize: 12,
    fontWeight: '500',
  },
});
